import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { PerformanceInsights } from '../types';
import { Users, TrendingUp, AlertTriangle, Target, Award, ArrowUp } from 'lucide-react';

interface LeadershipPipelineTabProps {
  insights: PerformanceInsights;
}

const LeadershipPipelineTab = ({ insights }: LeadershipPipelineTabProps) => {
  const pipelineData = insights.leadershipPipelineIntelligence;

  if (!pipelineData) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-400 text-lg">No leadership pipeline data available</p>
        </div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Row 1, Column 1: Hierarchical Succession Matrix */}
      <Card className="bg-gradient-to-r from-blue-900/20 to-purple-900/20 border-blue-500/30">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            Succession Recommendations
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4 max-h-96 overflow-y-auto">
            {pipelineData.hierarchicalSuccession?.map((succession, index) => {
              // Fix hierarchy progression based on current level
              let nextLevel = succession.nextLevelRole;
              let currentLevel = succession.currentLevel;
              
              // Correct hierarchy: Team Member -> Team Lead -> Manager -> Director
              if (succession.currentLevel === 'Team Member') {
                nextLevel = 'Team Lead';
              } else if (succession.currentLevel === 'Team Lead') {
                nextLevel = 'Manager';
              } else if (succession.currentLevel === 'Manager') {
                nextLevel = 'Director';
              }
              
              return (
                <div key={index} className="bg-secondary/30 rounded-lg p-4 border border-blue-500/20">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h4 className="text-white font-semibold">{succession.employee}</h4>
                      <p className="text-gray-300 text-sm">
                        {currentLevel} → {nextLevel}
                      </p>
                    </div>
                    <div className="text-right">
                      <div className="flex items-center gap-2">
                        <span className={`text-sm font-medium ${
                          succession.readinessScore > 80 ? 'text-green-400' : 
                          succession.readinessScore > 60 ? 'text-yellow-400' : 'text-red-400'
                        }`}>
                          {succession.readinessScore}% Ready
                        </span>
                        <ArrowUp className={`h-4 w-4 ${
                          succession.readinessScore > 80 ? 'text-green-400' : 
                          succession.readinessScore > 60 ? 'text-yellow-400' : 'text-red-400'
                        }`} />
                      </div>
                      <p className="text-gray-400 text-xs">{succession.timeToReadiness}</p>
                    </div>
                  </div>
                  
                  <div className="mb-3">
                    <div className="w-full bg-gray-700 rounded-full h-2">
                      <div 
                        className={`h-2 rounded-full ${
                          succession.readinessScore > 80 ? 'bg-green-500' : 
                          succession.readinessScore > 60 ? 'bg-yellow-500' : 'bg-red-500'
                        }`}
                        style={{ width: `${succession.readinessScore}%` }}
                      />
                    </div>
                  </div>

                  <div>
                    <p className="text-gray-300 text-sm mb-1">Development Gaps:</p>
                    <div className="flex flex-wrap gap-2">
                      {succession.developmentGaps?.map((gap, gapIndex) => (
                        <span key={gapIndex} className="bg-orange-500/20 text-orange-400 px-2 py-1 rounded text-xs">
                          {gap}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Row 2, Column 1: Performance Trajectory Analysis */}
      <Card className="bg-gradient-to-r from-purple-900/20 to-pink-900/20 border-purple-500/30">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            Performance Trends Analysis
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4 max-h-96 overflow-y-auto">
            {pipelineData.performanceTrajectoryAnalysis?.map((trajectory, index) => (
              <div key={index} className="bg-secondary/30 rounded-lg p-4 border border-purple-500/20">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h4 className="text-white font-semibold">{trajectory.employee}</h4>
                    <p className="text-gray-300 text-sm">
                      Trend: <span className={`font-medium ${
                        trajectory.trend === 'Ascending' ? 'text-green-400' :
                        trajectory.trend === 'Stable' ? 'text-blue-400' : 'text-red-400'
                      }`}>
                        {trajectory.trend}
                      </span>
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-white font-semibold">
                      Projected: {trajectory.projectedPerformance.toFixed(1)}
                    </p>
                    <p className="text-gray-400 text-sm">
                      {trajectory.confidenceLevel}% confidence
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <span className="text-gray-400 text-sm">Performance History:</span>
                  <div className="flex gap-1">
                    {trajectory.historicalPerformance?.map((perf, perfIndex) => (
                      <div key={perfIndex} className="flex flex-col items-center">
                        <div 
                          className={`w-3 h-8 rounded ${
                            perf >= 3.5 ? 'bg-green-500' :
                            perf >= 2.5 ? 'bg-yellow-500' : 'bg-red-500'
                          }`}
                          style={{ height: `${(perf / 4) * 32}px` }}
                        />
                        <span className="text-xs text-gray-400 mt-1">{perf.toFixed(1)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Row 1, Column 2: Leadership Readiness Heatmap */}
      <Card className="bg-gradient-to-r from-green-900/20 to-teal-900/20 border-green-500/30">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            Leadership Capacity Readiness
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 max-h-96 overflow-y-auto">
            {pipelineData.leadershipReadinessHeatmap?.map((readiness, index) => (
              <div key={index} className="bg-secondary/30 rounded-lg p-4 border border-green-500/20">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-white font-semibold">{readiness.role}</h4>
                    <p className="text-gray-300 text-sm">
                      {readiness.readyCandidates} ready / {readiness.totalNeeded} needed
                    </p>
                  </div>
                  <div className="text-right">
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                      readiness.riskLevel === 'Low' ? 'bg-green-500/20 text-green-400' :
                      readiness.riskLevel === 'Medium' ? 'bg-yellow-500/20 text-yellow-400' :
                      readiness.riskLevel === 'High' ? 'bg-orange-500/20 text-orange-400' :
                      'bg-red-500/20 text-red-400'
                    }`}>
                      {readiness.riskLevel} Risk
                    </span>
                  </div>
                </div>
                
                <div className="mt-3">
                  <div className="w-full bg-gray-700 rounded-full h-3">
                    <div 
                      className={`h-3 rounded-full ${
                        readiness.riskLevel === 'Low' ? 'bg-green-500' :
                        readiness.riskLevel === 'Medium' ? 'bg-yellow-500' :
                        readiness.riskLevel === 'High' ? 'bg-orange-500' : 'bg-red-500'
                      }`}
                      style={{ width: `${Math.min(100, (readiness.readyCandidates / readiness.totalNeeded) * 100)}%` }}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Row 2, Column 2: Critical Role Risk Assessment */}
      <Card className="bg-gradient-to-r from-red-900/20 to-orange-900/20 border-red-500/30">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            Critical Role Risk Assessment
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4 max-h-96 overflow-y-auto">
            {pipelineData.criticalRoleRisks?.map((risk, index) => (
              <div key={index} className="bg-secondary/30 rounded-lg p-4 border border-red-500/20">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h4 className="text-white font-semibold">{risk.role}</h4>
                    <p className="text-gray-300 text-sm">Current: {risk.currentIncumbent}</p>
                  </div>
                  <div className="text-right">
                    <div className={`text-lg font-bold ${
                      risk.riskScore > 75 ? 'text-red-400' :
                      risk.riskScore > 50 ? 'text-orange-400' :
                      risk.riskScore > 25 ? 'text-yellow-400' : 'text-green-400'
                    }`}>
                      {risk.riskScore.toFixed(0)}%
                    </div>
                    <p className="text-gray-400 text-sm">Risk Score</p>
                  </div>
                </div>

                <div className="mb-3">
                  <p className="text-gray-300 text-sm mb-1">
                    Backup Candidates: <span className="font-medium">{risk.backupCandidates}</span>
                  </p>
                  <div className="w-full bg-gray-700 rounded-full h-2">
                    <div 
                      className={`h-2 rounded-full ${
                        risk.riskScore > 75 ? 'bg-red-500' :
                        risk.riskScore > 50 ? 'bg-orange-500' :
                        risk.riskScore > 25 ? 'bg-yellow-500' : 'bg-green-500'
                      }`}
                      style={{ width: `${risk.riskScore}%` }}
                    />
                  </div>
                </div>

                <div>
                  <p className="text-gray-300 text-sm mb-2">Recommendations:</p>
                  <ul className="space-y-1">
                    {risk.recommendations?.map((rec, recIndex) => (
                      <li key={recIndex} className="text-gray-400 text-sm flex items-start gap-2">
                        <span className="text-primary">•</span>
                        {rec}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default LeadershipPipelineTab;
